import { useRef, useState } from 'react';
import { Loader2, PencilLine, ImagePlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useData } from '@/hooks/useData';
import { squareLogoBlob } from '@/lib/logoImage';

const MAX_BYTES = 2 * 1024 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function LogoUpload({ value, onChange }) {
  const { user } = useData();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error('Use uma imagem PNG, JPG ou WebP.');
    if (file.size > MAX_BYTES) return toast.error('A imagem precisa ter até 2 MB.');
    if (!user) return toast.error('Entre na sua conta para enviar a logo.');

    setBusy(true);
    try {
      const blob = await squareLogoBlob(file, 250);
      const path = `${user.id}/logo.png`;
      const { error } = await supabase.storage
        .from('company-logos')
        .upload(path, blob, { upsert: true, contentType: 'image/png', cacheControl: '3600' });
      if (error) throw error;
      const { data } = supabase.storage.from('company-logos').getPublicUrl(path);
      onChange(`${data.publicUrl}?v=${Date.now()}`);
      toast.success('Logo enviada');
    } catch (ex) {
      toast.error(ex.message || 'Não foi possível enviar a logo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      {value ? (
        <img src={value} alt="Logo da empresa" className="h-20 w-20 shrink-0 rounded-xl border border-white/10 object-cover" />
      ) : (
        <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl border border-dashed border-white/15 text-muted-foreground">
          <ImagePlus className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 space-y-2">
        <div className="text-sm font-medium">Logo da empresa</div>
        <p className="text-[11px] text-muted-foreground">PNG, JPG ou WebP até 2 MB. A imagem é ajustada para 250×250 px.</p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="border border-white/10"
            disabled={!user || busy}
            onClick={() => inputRef.current?.click()}
            data-testid="logo-upload-button"
          >
            {busy ? <Loader2 className="animate-spin" /> : <PencilLine />} {value ? 'Trocar logo' : 'Enviar logo'}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onChange('')}>
              <Trash2 /> Remover
            </Button>
          )}
        </div>
        {!user && <p className="text-[11px] text-amber-200">Disponível depois de entrar na conta, em Meu perfil empresarial.</p>}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="logo-file-input" />
      </div>
    </div>
  );
}